import React, {
	useEffect,
	useLayoutEffect,
	useState,
	useRef,
} from 'react';
import {
	Popover,
	Typography,
} from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExchange } from '@fortawesome/pro-light-svg-icons';
import classNames from 'classnames';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { InputNumberProps } from 'antd/lib';
import { Currencies } from '@shared/utils/constants';
import isDev from '@shared/utils/isDev';
import { formatCurrency } from '@shared/utils/currency';
import { Values } from '@shared/utils/objectEnums';
import NumericInput from '@client/components/NumericInput';
import { getExchangeRates } from '@client/lib/api';
import { useGlobalState } from '@client/lib/globalState';
import { ExtendUnique } from '@client/utils/ExtendUnique';
import styles from './styles/MultiCurrencyInput.module.css';
import Select from './Select';

export type MultiCurrencyValueObject = {
	currency: Values<typeof Currencies> | undefined;
	value: number | undefined;
	exchangeRate: number | undefined;
};

export type MultiCurrencyInputProps = ExtendUnique<{
	baseCurrency: Values<typeof Currencies>;
	onChange?: (newValue: MultiCurrencyValueObject) => void;
	inputClassName?: string;
	wrapperClassName?: string;
	inputValues?: MultiCurrencyValueObject;
	formValues?: {} | undefined;
	disabled?: boolean;
	allowNegative?: boolean;
	'data-testid'?: string;
}, InputNumberProps<number>>;

const MultiCurrencyInput = ({
	allowNegative = false,
	baseCurrency,
	onChange,
	inputClassName = undefined,
	wrapperClassName,
	inputValues,
	disabled = false,
	value: formValues,
	...props
}: MultiCurrencyInputProps) => {
	const [exchangeRates, setExchangeRates] = useGlobalState(`exchange-rate-${baseCurrency}`);
	const [stackElements, setStackElements] = useState(false);

	const inputFieldRef = useRef<HTMLInputElement>(null);
	const containerRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const refreshExchangeRates = async () => {
			const rates = await getExchangeRates(baseCurrency);
			setExchangeRates(rates);
		};

		if (exchangeRates == null) {
			refreshExchangeRates();
		}
	}, [baseCurrency, exchangeRates, setExchangeRates]);

	if (isDev() && baseCurrency == null) {
		throw new Error('A base currency must be specified');
	}

	// We sometimes use MultiCurrencyInputs in Ant Design Forms, where values are passed to the
	// value prop - sometimes we DO NOT want to use the form value,
	// but instead set the values manually
	// If we've passed inputValues but formValues also exist,
	// we use inputValues, otherwise we use formValues
	// Decide what value object to use:
	// 1) InputValues always wins if provided
	// 2) Else if formValues is an object, use that (edit flows controlled by our Forms)
	// 3) Else fall back to baseCurrency + optional numeric value
	const valuesToUse = inputValues != null ? inputValues : formValues;

	let valueObj: Partial<MultiCurrencyValueObject> = {
		currency: baseCurrency,
		...(typeof valuesToUse === 'object' ? valuesToUse : {}),
	};

	if (valueObj.currency == null) {
		valueObj.currency = baseCurrency;
	}

	const { value, currency } = valueObj;

	const exchangeRate = valueObj.exchangeRate;
	const effectiveRate = exchangeRate ?? 1;

	const changeValue = (newValue: Partial<MultiCurrencyValueObject>) => {
		if (newValue.currency != null && newValue.currency !== valueObj.currency) {
			const rate = exchangeRates?.[newValue.currency as keyof typeof exchangeRates];
			newValue.exchangeRate = rate as unknown as number | undefined;
		}

		if (onChange != null) {
			onChange({
				currency: newValue.currency ?? valueObj.currency,
				value: newValue.value === null ? undefined : newValue.value,
				exchangeRate: newValue.exchangeRate ?? effectiveRate,
			});
		}
	};

	useEffect(() => {
		if (exchangeRates == null || currency == null || valueObj.exchangeRate != null) {
			return;
		}

		if (currency === baseCurrency) {
			changeValue({ exchangeRate: 1 });

			return;
		}

		const rate = exchangeRates[currency as keyof typeof exchangeRates];
		changeValue({ exchangeRate: rate as unknown as number | undefined });
	}, [baseCurrency, exchangeRates, currency, valueObj.exchangeRate]);

	const currencyInput = (
		<div
			className={styles.multiCurrencySelector}
		>
			<Select
				key={currency ?? baseCurrency}
				disabled={disabled}
				onSelect={(v) => changeValue({ currency: v as Values<typeof Currencies> })}
				value={currency ?? baseCurrency}
				showSearch
				options={Object.values(Currencies).map((c) => ({
					label: c,
					value: c,
				}))}
				data-testid="currency-selector"
			/>
		</div>
	);

	const exchangeRatePopover = (
		<Popover
			className={classNames({ [styles.stackExchangeRate]: stackElements })}
			content={(
				<>
					Exchange rate
					<br />
					<NumericInput
						onChange={(v) => changeValue({ exchangeRate: v ?? undefined })}
						value={effectiveRate}
						disabled={disabled}
						placeholder="Exchange Rate"
						addonAfter={`${effectiveRate} ${currency ?? baseCurrency} = 1 ${baseCurrency}`}
					/>
				</>
			)}
			title="Override Exchange Rate"
			trigger="click"
		>
			<div>
				<FontAwesomeIcon
					icon={faExchange as IconProp}
					size="1x"
				/>
			</div>
		</Popover>
	);

	useLayoutEffect(() => {
		const container = containerRef.current;

		const setIsStacked = () => {
			if (inputFieldRef?.current?.clientWidth) {
				setStackElements(inputFieldRef?.current?.clientWidth < 250);
			}
		};

		const observer = new ResizeObserver(setIsStacked);

		if (container != null) {
			observer.observe(container);
		}

		setIsStacked();

		return () => {
			if (container != null) {
				observer.unobserve(container);
			}
		};
	}, [inputFieldRef, containerRef]);

	const numericInput = (
		<NumericInput
			addonBefore={!stackElements && currencyInput}
			addonAfter={!stackElements && exchangeRatePopover}
			className={classNames(inputClassName, styles.clickableAddon)}
			disabled={disabled}
			min={allowNegative ? undefined : 0}
			onChange={(v) => changeValue({ value: v ?? undefined })}
			value={value}
			{...props}
		/>
	);

	const convertedCurrency = (
		value != null &&
		value !== 0 &&
		effectiveRate !== 1 && (
			<Typography.Text
				italic
				type="secondary"
				className={stackElements ? styles.stackedConvertedAmount : styles.convertedAmount}
			>
				{`≈ ${formatCurrency(value / effectiveRate, baseCurrency)}`}
			</Typography.Text>
		)
	);

	return (
		<div ref={containerRef}>
			<div
				className={classNames([{
					[styles.multiCurrencyInputWrapperStacked]: stackElements,
					[styles.multiCurrencyInputWrapper]: !stackElements,
				}, wrapperClassName])}
				ref={inputFieldRef}
			>
				{numericInput}
				{stackElements ? (
					<div className={styles.currencyActionsContainer}>
						{currencyInput}
						{exchangeRatePopover}
					</div>
				) : (<br />)}
			</div>
			{convertedCurrency}
		</div>
	);
};

export default MultiCurrencyInput;
